import { format } from "date-fns";
import { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import Button from "@ingka/button";
import CalendarIcon from "@ingka/ssr-icon/paths/calendar";
import Text from "@ingka/text";
import { useBoolean } from "@pdpp/lib-react";

import { css } from "../__generated-styled-system/css";
import { DatePickerRange } from "../components/DatePickerRange";
import { useGetBudgetStatus } from "../features/budget/useGetBudgetStatus";
import { orgId$, selectedUnit$ } from "../features/org/state";
import { period$, periodString } from "../features/period";
import { getHeadcountRequest } from "../features/plan/api";
import { view$ } from "../features/plan/state";
import { PlanView } from "../types/api";
import { View } from "../types/client";

const periodWrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "space50",
});

const periodTextStyles = css({
  marginLeft: "space100!",
});

export const Period: FC = () => {
  const [t, i18next] = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const signalPeriod = period$.value;
  const selectedUnit = selectedUnit$.value;
  const orgNodeId = orgId$.value ?? "";

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState<Readonly<[Date, Date]>>(signalPeriod);

  const [isPopoverShown, , hidePopover, togglePopover] = useBoolean(false);

  useEffect(() => {
    setValue(signalPeriod);
  }, [signalPeriod]);

  const handleDatePickerChange = useCallback((start: Date, end: Date) => {
    setValue([start, end]);
  }, []);

  const handleApply = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    hidePopover();
    const [startDate, endDate] = value ?? [];

    const newPeriod: Readonly<[Date, Date]> = [startDate, endDate];
    period$.value = [...newPeriod];

    view$.value = View.Day;

    const params = new URLSearchParams(window.location.search);
    getHeadcountRequest({
      view: View.Week,
      orgNodeId,
      period: [
        format(period$.value[0], "yyyy-MM-dd"),
        format(period$.value[1], "yyyy-MM-dd"),
      ],
    });
    params.set(
      "period",
      newPeriod.map((v) => v.toLocaleString("sv").slice(0, 10)).toString(),
    );
    params.set("view", PlanView.Day.toString());
    setSearchParams(params.toString());
  };

  useGetBudgetStatus({
    unitType: String(selectedUnit?.attributes?.["businessUnitType"] ?? ""),
    unitId: selectedUnit?.publicId ?? "",
    startDate: format(period$.value[0], "yyyy-MM-dd"),
    endDate: format(period$.value[1], "yyyy-MM-dd"),
  });

  return (
    <div className={periodWrapperStyles}>
      <Text tagName="span" textStyle="Body.S" className={periodTextStyles}>
        {t("labels.period")}
      </Text>
      <Button
        ref={setAnchorEl}
        size="small"
        type="tertiary"
        text={periodString(
          period$.value[0],
          period$.value[1],
          i18next.language,
        )}
        iconPosition="leading"
        ssrIcon={CalendarIcon}
        onClick={togglePopover}
        data-analytics="plan/period/select-date"
      />
      {isPopoverShown && (
        <DatePickerRange
          selectedRange={value}
          anchorEl={anchorEl}
          onChange={handleDatePickerChange}
          locale={i18next.language}
          verticalAlignment="top"
          horizontalAlignment="right"
          horizontalPosition="right"
          verticalPosition="bottom"
          onApply={handleApply}
          clickAwayHandler={hidePopover}
        />
      )}
    </div>
  );
};
